import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { DeviceInput } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { BaseStationInput, DeviceDetailSaveModel } from '../models/device-detail-save.model';
import { deleteDeviceMutation } from '../queries/delete-device.graphql';
import { DeleteDeviceVariables } from '../queries/generated/DeleteDevice';
import { SaveDevice, SaveDeviceVariables } from '../queries/generated/SaveDevice';
import { SaveDeviceMutation } from '../queries/save-device.graphql';

@Injectable({ providedIn: 'root' })
export class DeviceDetailApiService {
   constructor(private apiService: LcsApiService) {}

   save(deviceDetail: DeviceDetailSaveModel, fields: (keyof DeviceInput)[]): Observable<ExecutionResult<SaveDevice>> {
      const deletedBaseStationSteps = fields.includes('baseStationHandsets')
         ? getDeletedBaseStationSteps(deviceDetail.baseStationHandsets ?? [])
         : [];

      let combinedFields: string[] = fields;
      if (fields.includes('baseStationHandsets')) {
         combinedFields = [...fields, ...formatDeviceFields('baseStationHandsets', fields)].filter(
            (field) => field !== 'baseStationHandsets'
         );
      }

      const updatedDeviceDetail = updateDeviceDetail(deviceDetail);

      return this.apiService.query<SaveDevice, SaveDeviceVariables>({
         query: SaveDeviceMutation,
         variables: {
            device: {
               ...updatedDeviceDetail,
               fields: combinedFields,
            },
            deletedHandsetKeysIds: deletedBaseStationSteps,
            hasDeletedHandsets: deletedBaseStationSteps.length > 0,
         },
      });
   }

   deleteDevice(deviceId: number): Observable<ExecutionResult<void>> {
      return this.apiService.query<void, DeleteDeviceVariables>({
         query: deleteDeviceMutation,
         variables: {
            deviceId,
         },
      });
   }
}

type deviceInputFields = keyof DeviceInput;

function formatDeviceFields(key: string, embedFields: deviceInputFields[]) {
   const deviceHandsetsFields: string[] = ['baseStationHandsetId', 'phoneId', 'deviceId', 'sortId'];
   const formattedFields: string[] = [];
   if (key) {
      for (const embedField of embedFields) {
         if (embedField === 'baseStationHandsets') {
            formattedFields.push(
               ...deviceHandsetsFields.map((keyField) => {
                  return `${embedField}.${keyField}` as deviceInputFields;
               })
            );
         }
      }
   }

   return formattedFields;
}

function updateDeviceDetail(deviceDetail) {
   return {
      ...deviceDetail,
      baseStationHandsets:
         deviceDetail.baseStationHandsets
            ?.filter((handset) => !handset.isDeleted)
            .map((handset) => {
               const { isDeleted, ...properties } = handset;
               return {
                  ...properties,
                  baseStationHandsetId: null,
               };
            }) ?? [],
   };
}

export function getDeletedBaseStationSteps(steps: BaseStationInput[]): number[] {
   return steps
      .filter((handset) => handset.baseStationHandsetId !== null && handset.baseStationHandsetId > 0)
      .map((handset) => handset.baseStationHandsetId);
}
